import React, { useState, useRef, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment';

import EventIcon from '@material-ui/icons/Event';

import List from '~/containers/List';
import ModalDelete from '~/components/ModalDelete';

import {
  newMedicalAppointments,
  showMedicalAppointments,
  editMedicalAppointments,
} from '~/config/routes';

import Request from '~/providers/Request';
import { AbilityContext } from '~/contexts/Can';

import MedicalAppointmentsContext from '../context';

function MedicalAppointmentsList({ update }) {
  const { t } = useTranslation();
  const [modalInfo, setModalInfo] = useState({ show: false });
  const { delRecord } = useContext(MedicalAppointmentsContext);
  const ability = useContext(AbilityContext);
  const listEl = useRef(null);
  const [specialtyNames, setSpecialtyNames] = useState(null);

  useEffect(() => {
    Request.get(
      '/admin/specialties',
      { per_page: 1000, sorter: 'name.asc' },
      { normalizeJSON: true }
    )
      .then(({ data }) =>
        setSpecialtyNames(
          Object.fromEntries(data.map(({ name }) => [name.trim(), name.trim()]))
        )
      )
      .catch(() => setSpecialtyNames({}));
  }, []);

  const priority = {
    low: t('medicalAppointments.priority.low'),
    medium: t('medicalAppointments.priority.medium'),
    high: t('medicalAppointments.priority.high'),
  };

  const status = {
    new: t('medicalAppointments.status.new'),
    pre_appointment: t('medicalAppointments.status.pre_appointment'),
    wait_room: t('medicalAppointments.status.wait_room'),
    wait_room_scheduled: t('medicalAppointments.status.wait_room_scheduled'),
    ongoing: t('medicalAppointments.status.ongoing'),
    on_schedule: t('medicalAppointments.status.on_schedule'),
    on_approval: t('medicalAppointments.status.on_approval'),
    in_consultation: t('medicalAppointments.status.in_consultation'),
    finished: t('medicalAppointments.status.finished'),
  };

  const priorityColor = {
    low: '#03D100',
    medium: '#FF6B00',
    high: '#E00023',
  };

  const statusColor = {
    new: '#AC92EA',
    pre_appointment: '#AC92EA',
    wait_room: '#AC92EA',
    wait_room_scheduled: '#2182F6',
    on_schedule: '#AC92EA',
    on_approval: '#AC92EA',
    in_consultation: '#AC92EA',
    ongoing: '#F9D159',
    finished: '#999999',
  };

  const trAttr = (attr) => t(`medicalAppointments.attributes.${attr}`);
  const trExtra = (extra) => t(`medicalAppointments.extras.${extra}`);

  function handleDeletionModal(id, title) {
    setModalInfo({ show: true, id, title });
  }

  async function handleDelete() {
    try {
      await Request.del(`/admin/medical_appointment/${modalInfo.id}`);
      delRecord(modalInfo.id);
      listEl.current.onQueryChange();
      toast.success(t('delete-toast-success'));
    } catch (e) {
      toast.error(t('delete-toast-fail'));
    } finally {
      setModalInfo({ show: false });
    }
  }

  const actions = ['search', 'filter'];

  if (ability.can('new', 'MedicalAppointments')) {
    actions.push('new');
  }
  if (ability.can('edit', 'MedicalAppointments')) {
    actions.push('edit');
  }
  if (ability.can('delete', 'MedicalAppointments')) {
    actions.push('del');
  }
  if (ability.can('show', 'MedicalAppointments')) {
    actions.push('show');
  }

  if (!specialtyNames) return null;

  return (
    <div>
      <List
        deleteAction={handleDeletionModal}
        routeToNew={newMedicalAppointments}
        routeToEdit={editMedicalAppointments}
        routeToShow={showMedicalAppointments}
        listEl={listEl}
        path={'/admin/medical_appointment/scheduled'}
        actions={actions}
        paperProps={{
          Icon: EventIcon,
          iconColor: 'blue',
          title: t('medicalAppointments.tabs.scheduled_appointments'),
        }}
        columns={[
          {
            title: trAttr('id'),
            field: 'id',
            type: 'numeric',
            precision: 0,
            includeThousandsSeparator: false,
          },
          { title: trAttr('user'), field: 'user_name', type: 'string' },
          {
            title: trExtra('doctor_consulted'),
            field: 'record_filename',
            type: 'boolean',
          },
          {
            field: 'status',
            type: 'string',
            title: trAttr('status'),
            // eslint-disable-next-line react/display-name
            render: (rowData) => {
              return (
                <label
                  style={{
                    backgroundColor: statusColor[rowData.status],
                    padding: 4,
                    color: 'white',
                    borderRadius: 4,
                    fontWeight: 500,
                  }}
                >{`${status[rowData.status]}`}</label>
              );
            },
          },
          { title: trAttr('doctor'), field: 'doctor_name', type: 'string' },
          {
            title: trAttr('specialty'),
            field: 'specialty_name',
            type: 'string',
            lookup: specialtyNames,
            render: (rowData) => rowData.specialty_name,
          },
          { title: trAttr('notes'), field: 'notes', type: 'string' },
          {
            field: 'priority',
            type: 'string',
            title: trAttr('priority'),
            // eslint-disable-next-line react/display-name
            render: (rowData) => {
              return (
                <label
                  style={{
                    backgroundColor: priorityColor[rowData.priority],
                    padding: 4,
                    color: 'white',
                    borderRadius: 4,
                    fontWeight: 500,
                  }}
                >{`${priority[rowData.priority]}`}</label>
              );
            },
          },
          {
            title: trAttr('schedule_appointment_at'),
            field: 'schedule_appointment_at',
            type: 'datetime',
          },
          {
            title: trAttr('created_at'),
            field: 'created_at',
            type: 'datetime',
          },
          {
            title: trAttr('updated_at'),
            field: 'updated_at',
            type: 'datetime',
          },
        ]}
        context={MedicalAppointmentsContext}
        recordMap={(record) => ({
          ...record,
          schedule_appointment_at: record.schedule_appointment_at
            ? moment(record.schedule_appointment_at).format('DD/MM/YYYY HH:mm')
            : null,
          created_at: record.created_at
            ? moment(record.created_at).format('DD/MM/YYYY HH:mm')
            : null,
          updated_at: record.updated_at
            ? moment(record.updated_at).format('DD/MM/YYYY HH:mm')
            : null,
        })}
        fetchFlag={update}
      />

      <ModalDelete
        id={modalInfo.id}
        name={modalInfo.title}
        modal={modalInfo.show}
        deleteAction={handleDelete}
        onClose={() => setModalInfo({ show: false })}
      />
    </div>
  );
}

export default React.memo(MedicalAppointmentsList);
